<template>
  <div id="profile" class="wrrapper">
    <page-heading title="Profile" border back></page-heading>

    <div class="profile-shell">
      <aside class="profile-identity">
        <div class="identity-card">
          <label for="upload-photo" class="avatar-upload cursor">
            <div
              :class="[uploadProgress ? 'img-container' : '', 'avatar-wrap']"
            >
              <img :src="profile" alt="Avatar" class="image avatar-image" />
              <div class="avatar-status"></div>
              <div class="middle">
                <div class="upload--progress">
                  {{ uploadProgress ? uploadProgress + "%" : "" }}
                </div>
              </div>
            </div>
          </label>

          <Upload
            label="Upload Picture"
            @update-picture="handleFileUpload"
            for="upload-photo"
            class="profile-upload-control"
          />

          <div class="identity-copy">
            <Heading
              class="capitalize profile-name"
              :heading="user.full_name"
            />
            <p class="profile-role capitalize">{{ displayRole }}</p>
          </div>

          <div class="profile-summary">
            <div class="summary-item">
              <span class="summary-icon mdi mdi-email-outline"></span>
              <span>{{ user.email || "No email" }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-icon mdi mdi-phone-outline"></span>
              <span>{{ user.mobile_no || "No phone" }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-icon mdi mdi-map-marker-outline"></span>
              <span>{{ user.city || "No city" }}</span>
            </div>
          </div>

          <div class="profile-note">
            <div class="note-head">
              <h2>About</h2>
              <button
                class="text-action note-action"
                type="button"
                @click="readonly = !readonly"
              >
                {{ readonly ? "Edit" : "Done" }}
              </button>
            </div>
            <p class="description" v-if="readonly">
              {{ description || "No profile note added." }}
            </p>
            <TextArea
              v-if="!readonly"
              styles="border description"
              :readonly="readonly"
              :value="description"
              @input="handleDescription"
              :rows="rows"
            />
          </div>
        </div>
      </aside>

      <main class="profile-main">
        <section class="profile-section details-section">
          <div class="section-head">
            <div>
              <p class="section-kicker">Account</p>
              <h2>Personal Details</h2>
            </div>
            <Button
              title="Update"
              className="button--primary profile-primary-action"
              :loader="loading"
              :onClick="handleUpdate"
            />
          </div>

          <div class="details-grid">
            <Input
              class="profile-field"
              label="Full Name"
              type="text"
              v-model="user.full_name"
            />
            <Input
              class="profile-field"
              label="Email Address"
              type="text"
              v-model="user.email"
            />
            <Input
              class="profile-field"
              label="Phone Number"
              type="number"
              v-model="user.mobile_no"
            />
            <Input
              class="profile-field"
              label="CNIC"
              type="number"
              v-model="user.cnic"
            />
            <Input
              class="profile-field profile-field--wide"
              label="Address"
              type="text"
              v-model="user.address"
            />
            <Input
              class="profile-field"
              label="City"
              type="text"
              v-model="user.city"
            />
            <Radio
              class="profile-field profile-radio"
              outerLabel="Gender"
              type="radio"
              name="custom"
              v-model="userGender"
              :options="gender"
            />
          </div>
        </section>

        <section class="profile-section security-section">
          <div class="section-head">
            <div>
              <p class="section-kicker">Security</p>
              <h2>Access Controls</h2>
            </div>
          </div>

          <div class="security-list">
            <div class="security-row">
              <div class="security-icon">
                <span class="mdi mdi-shield-key-outline"></span>
              </div>
              <div class="security-copy">
                <h3>Two Factor Authentication</h3>
                <p>
                  <span v-if="is2faActive" class="active">Active</span>
                  <span v-if="!is2faActive" class="inactive">Not Active</span>
                </p>
              </div>
              <Button
                v-if="!is2faActive"
                title="Enable 2FA"
                className="button--primary security-button"
                :onClick="openModel"
              />
              <Button
                v-else
                title="Disable 2FA"
                className="button--primary security-button"
                :onClick="disable2fa"
                :loader="disable2faLoading"
              />
            </div>

            <div class="security-row">
              <div class="security-icon">
                <span class="mdi mdi-lock-reset"></span>
              </div>
              <div class="security-copy">
                <h3>Password</h3>
                <p class="password-mask">**********</p>
              </div>
              <Button
                title="Change Password"
                className="button--primary security-button"
                :onClick="openPasswordModal"
              />
            </div>
          </div>
        </section>

        <section
          v-if="userSession && userSession.length > 0"
          class="profile-section user-session"
        >
          <div class="section-head">
            <div>
              <p class="section-kicker">Sessions</p>
              <h2>Active Sessions</h2>
            </div>
            <span class="session-count">{{ userSession.length }}</span>
          </div>

          <ul class="session-list">
            <li
              v-for="(session, index) in sortedUserSessions"
              :key="session.sessionId || index"
              class="session-card"
            >
              <div class="session-card-head">
                <div class="session-device">
                  <span class="mdi mdi-laptop session-device-icon"></span>
                  <div>
                    <h3>{{ session.browser || "Unknown Browser" }}</h3>
                    <p>{{ session.os || "Unknown OS" }}</p>
                  </div>
                </div>
                <span
                  v-if="session.sessionId === currentSessionId"
                  class="current-session"
                >
                  Current
                </span>
              </div>

              <div class="session-meta">
                <div>
                  <span>IP</span>
                  <strong>{{ session.ip || "-" }}</strong>
                </div>
                <div>
                  <span>Location</span>
                  <strong>{{ session.location || "-" }}</strong>
                </div>
                <div>
                  <span>Last Active</span>
                  <strong>{{ formatTimestamp(session.timestamp) }}</strong>
                </div>
              </div>

              <div class="session-actions">
                <Button
                  title="Logout"
                  className="button--primary session-logout"
                  :onClick="() => logoutCurrentSession(session)"
                  :loader="sessionLogoutLoading === session.sessionId"
                />
              </div>
            </li>
          </ul>
        </section>
      </main>

      <modal
        title="Two Factor Authentication"
        name="downloadModel"
        class="custom-modal"
        height="auto"
        width="50%"
        :adaptive="true"
      >
        <template v-slot:content>
          <div class="two-factor-modal">
            <div class="qr-code">
              <img :src="qrCodeUrl" alt="Two factor authentication QR code" />
            </div>
            <label for="username">
              <span>Verification code</span>
            </label>
            <Input
              type="text"
              v-model="optCode"
              class="error mt-1"
              name="username"
              id="username"
            />
            <p class="error" v-if="otpError">
              Invalid OTP please try again
            </p>
            <Button
              :loader="verifyLoading"
              className="button--primary submit mt-3 modal-action"
              :onClick="verfyOtp"
              title="Verify OTP"
            ></Button>
          </div>
        </template>
      </modal>

      <modal
        title="Change Password"
        name="passwordModal"
        class="custom-modal"
        height="auto"
        width="40%"
        :adaptive="true"
      >
        <template v-slot:content>
          <div class="password-modal">
            <validation-observer ref="passwordObserver">
              <form @submit.prevent="handlePasswordChange">
                <validation-provider
                  vid="newPassword"
                  rules="required|min:6"
                  v-slot="{ errors }"
                >
                  <div class="show-passowrd mb-3">
                    <Input
                      class="mb-base custom-input"
                      label="New Password"
                      :error="errors[0]"
                      type="password"
                      v-model="newPassword"
                    />
                  </div>
                </validation-provider>
                <validation-provider
                  rules="required|confirmed:newPassword"
                  v-slot="{ errors }"
                >
                  <div class="show-passowrd mb-3">
                    <Input
                      class="mb-base custom-input"
                      label="Confirm New Password"
                      :error="errors[0]"
                      type="password"
                      v-model="confirmPassword"
                    />
                  </div>
                </validation-provider>
                <div class="flex justify-center mt-3">
                  <Button
                    :loader="passwordLoading"
                    className="button--primary submit modal-action"
                    :onClick="handlePasswordChange"
                    title="Update Password"
                  ></Button>
                </div>
              </form>
            </validation-observer>
          </div>
        </template>
      </modal>
    </div>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider } from "vee-validate";
import Heading from "@/components/Heading";
import Upload from "@/components/Upload";
import Input from "@/components/Input";
import { api } from "@/axios";
import Radio from "@/components/Radio";
import Button from "@/components/Button";
import TextArea from "@/components/TextArea";
import { formatGender, getGender } from "@/utils/helpers";
import { mapGetters, mapActions } from "vuex";
import Modal from "@/components/Modal";
import moment from "moment";
export default {
  components: {
    ValidationObserver,
    ValidationProvider,
    Heading,
    TextArea,
    Upload,
    Button,
    Radio,
    Input,
    Modal
  },
  data() {
    return {
      user: {},
      rows: 3,
      description: "",
      readonly: true,
      userSession: [],
      is2faActive: false,
      qrCodeUrl: "",
      disable2faLoading: false,
      sessionLogoutLoading: "",
      optCode: "",
      otpError: false,
      verifyLoading: false,
      passwordLoading: false,
      newPassword: "",
      confirmPassword: "",
      gender: [
        { id: "male", for: "male", label: "Male" },
        { id: "female", for: "female", label: "Female" }
      ]
    };
  },
  methods: {
    ...mapActions({
      uploadAttachment: "auth/uploadAttachment",
      handleUpdateUsers: "user/handleUpdateUsers",
      userDetails: "user/userDetails",
      resetUploadProgress: "auth/resetUploadProgress",
      validateSession: "auth/validateSession"
    }),
    handleFileUpload(file) {
      this.uploadAttachment({ file: file });
    },
    async verfyOtp() {
      try {
        this.verifyLoading = true;
        const payload = {
          userId: this.getUser.userId,
          otp: this.optCode
        };
        const res = await api.post("/2fa/verify", payload);
        if (res.data.isValid.isValid) {
          await this.userDetails();
          this.is2faActive = true;
          this.$toasted.success("2FA enabled successfully").goAway("3000");
          this.$modal.hide("downloadModel");
        } else {
          this.otpError = true;
          this.$toasted.error("Invalid OTP").goAway("3000");
        }
      } catch (error) {
        this.$toasted.error("Failed to verify OTP").goAway("3000");
      } finally {
        this.verifyLoading = false;
      }
    },
    async disable2fa() {
      try {
        this.disable2faLoading = true;
        const res = await api.delete("/2fa/disable2fa/" + this.getUser.userId);
        if (res.status == 200) {
          this.is2faActive = false;
          await this.userDetails();
          this.$toasted.success("2FA disabled successfully").goAway("3000");
        }
      } catch (error) {
        this.$toasted.error("Failed to disable 2FA").goAway("3000");
      } finally {
        this.disable2faLoading = false;
      }
    },
    handleDescription(val) {
      this.description = val;
    },
    async handlePasswordChange() {
      this.$refs.passwordObserver.validate().then(async success => {
        if (!success) {
          return;
        }
        try {
          this.passwordLoading = true;
          const user = {
            password: this.newPassword
          };
          await this.handleUpdateUsers({
            id: this.userInfo.id,
            user: user
          });
          this.$toasted.success("Password updated successfully").goAway("3000");
          this.$modal.hide("passwordModal");
          this.newPassword = "";
          this.confirmPassword = "";
        } catch (error) {
          this.$toasted.error("Failed to update password").goAway("3000");
        } finally {
          this.passwordLoading = false;
        }
      });
    },
    openPasswordModal() {
      this.$modal.show("passwordModal");
    },
    async logoutCurrentSession(session) {
      try {
        if (session.sessionId === this.currentSessionId) {
          const payload = {
            userId: this.getUser.userId,
            sessionId: session.sessionId
          };
          await this.validateSession({ vm: this, payload });
          this.$store.dispatch("auth/logout", { vm: this }).then(() => {
            this.$router.push("/");
          });
        } else {
          this.sessionLogoutLoading = session.sessionId;
          const payload = {
            userId: this.getUser.userId,
            sessionId: session.sessionId
          };
          await this.validateSession({ vm: this, payload });
          if (this.validateSessionStatus.validated === true) {
            this.$toasted.success("Logout successfully").goAway("3000");
            this.userSession = this.userSession.filter(
              item => item.sessionId != session.sessionId
            );
          }
        }
      } catch (error) {
        this.$toasted.error("Failed to logout session").goAway("3000");
      } finally {
        this.sessionLogoutLoading = "";
      }
    },
    async openModel() {
      const payload = {
        user: {
          id: this.getUser.userId
        }
      };
      const res = await api.post("/2fa/generate", payload);
      this.qrCodeUrl = res.data.qrCodeDataURL;
      this.$modal.show("downloadModel");
    },
    handleUpdate() {
      this.user.attachmentId = this.attachmentId;
      this.user.description = this.description;
      this.handleUpdateUsers({
        id: this.userInfo.id,
        user: this.user
      }).then(() => {
        this.$toasted.success("Profile updated succesfully").goAway("3000");
        this.resetUploadProgress();
        this.userDetails();
        this.readonly = true;
      });
    },
    formatTimestamp(timestamp) {
      const date = new Date(timestamp);
      return moment(date).fromNow();
    }
  },
  computed: {
    ...mapGetters({
      attachmentId: "auth/attachmentId",
      uploadProgress: "auth/uploadProgress",
      userInfo: "user/userInfo",
      loading: "loading",
      getUser: "user/getUser",
      auhtInfo: "auth/userInfo",
      validateSessionStatus: "auth/validateSessionStatus"
    }),
    userGender: {
      get() {
        return formatGender(this.userInfo && this.userInfo.gender);
      },
      set(val) {
        this.user.gender = getGender(val);
      }
    },
    displayRole() {
      const roles = (this.auhtInfo && this.auhtInfo.userRole) || [];
      if (!roles.length) {
        return "Staff";
      }
      if (roles.length > 1) {
        return "Super Admin";
      }
      return roles[0].name || "Staff";
    },
    currentSessionId() {
      return (
        this.auhtInfo &&
        this.auhtInfo.currentUserSession &&
        this.auhtInfo.currentUserSession.sessionId
      );
    },
    sortedUserSessions() {
      return this.userSession
        .slice()
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    },
    profile() {
      return (
        this?.userInfo?.attachment?.path || require("../assets/upload.png")
      );
    }
  },
  watch: {
    userInfo(val) {
      if (!val) {
        return;
      }
      const newObj = { ...val };
      delete newObj.attachment;
      this.user = newObj;
      this.description = val.description || "";
    },
    description(val) {
      if (val && val != null) {
        const newarray = val.split("\n");
        this.rows = Array.isArray(newarray) && newarray.length + 1;
      }
    }
  },
  async mounted() {
    await this.userDetails();
    this.userSession = this.getUser.userSession || [];
    this.is2faActive = this.getUser.is2fa;
  }
};
</script>

<style>
#profile {
  min-height: calc(100vh - 9rem);
}

#profile .customInput {
  margin-bottom: 0;
}

#profile .profile-field .input--wrrapper,
#profile .password-modal .input--wrrapper,
#profile .two-factor-modal .input--wrrapper {
  height: 42px;
  background: var(--surface);
}

#profile .profile-field input,
#profile .password-modal input,
#profile .two-factor-modal input {
  background: transparent;
}

#profile .profile-radio .type-input {
  gap: 10px 18px;
}

#profile .profile-radio .box {
  flex: unset;
  margin-top: 8px;
}

#profile .profile-radio label {
  color: var(--ink-800);
  font-weight: 500;
}

#profile .profile-upload-control {
  display: flex;
  justify-content: center;
  margin-top: 14px;
}

#profile .upload-picture {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  padding: 8px 14px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-sm);
  background: var(--surface);
  color: var(--accent-600);
  letter-spacing: 0;
  text-transform: none;
}

#profile .upload-picture:hover {
  border-color: var(--accent-600);
  background: var(--accent-050);
}

#profile .profile-name.heading {
  justify-content: center;
}

#profile .profile-name h3 {
  margin: 0 auto !important;
  max-width: 100%;
  color: var(--ink-900);
  font-size: var(--fs-h1);
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: var(--tracking-tight);
  text-align: center;
  text-transform: none;
  overflow-wrap: anywhere;
}

#profile .description {
  width: 100%;
  margin: 0;
  font-size: var(--fs-body);
  line-height: 1.65;
  color: var(--muted);
  letter-spacing: 0;
}

#profile .textarea {
  margin-top: 0;
}

#profile .textarea textarea.description {
  min-height: 120px;
  background: var(--surface);
}

#profile .border {
  border: 1px solid var(--line) !important;
}

#profile .modal-action {
  width: 100%;
  min-height: 42px;
}

#profile .modal-action span {
  width: 100%;
}

#profile .session-logout span {
  color: var(--accent-600);
  font-size: var(--fs-xs);
  line-height: 16px;
}

#profile .session-logout {
  border: 1px solid var(--line-strong);
  background: var(--surface);
  box-shadow: none;
}

#profile .session-logout:hover {
  border-color: var(--accent-600);
  background: var(--accent-050);
}

#profile .session-logout .loader {
  width: 16px;
  height: 16px;
}
</style>

<style scoped>
.profile-shell {
  display: grid;
  grid-template-columns: minmax(260px, 310px) minmax(0, 1fr);
  gap: 20px;
  align-items: start;
  padding: 22px;
}

.profile-identity {
  position: sticky;
  top: 86px;
}

.identity-card,
.profile-section {
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
  background: var(--surface);
}

.identity-card {
  padding: 24px 22px;
  text-align: center;
}

.avatar-upload {
  display: inline-block;
}

.avatar-wrap {
  position: relative;
  width: 132px;
  height: 132px;
  margin: 0 auto;
  padding: 5px;
  border-radius: 50%;
  background: var(--line);
}

.avatar-image {
  display: block;
  width: 100%;
  height: 100%;
  border: 4px solid var(--surface);
  border-radius: 50%;
  object-fit: cover;
  background: var(--surface-2);
  transition: opacity var(--dur) var(--ease);
}

.avatar-status {
  position: absolute;
  right: 15px;
  bottom: 16px;
  width: 14px;
  height: 14px;
  border: 3px solid var(--surface);
  border-radius: 50%;
  background: var(--success);
}

.avatar-wrap .middle {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  opacity: 0;
  pointer-events: none;
  transition: opacity var(--dur) var(--ease);
}

.img-container .avatar-image {
  opacity: 0.28;
}

.img-container .middle {
  opacity: 1;
}

.upload--progress {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 52px;
  min-height: 52px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  color: var(--ink-900);
  font-size: var(--fs-body);
  font-weight: 700;
  box-shadow: var(--shadow-md);
}

.identity-copy {
  margin-top: 16px;
}

.profile-role {
  margin: 6px 0 0;
  color: var(--muted);
  font-size: var(--fs-sm);
  font-weight: 600;
}

.profile-summary {
  display: grid;
  gap: 8px;
  margin-top: 18px;
  text-align: left;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  padding: 0;
  color: var(--text);
  font-size: var(--fs-sm);
}

.summary-item span:last-child {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.summary-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  color: var(--accent-600);
  font-size: 15px;
}

.profile-section {
  padding: 20px;
}

.profile-note {
  min-width: 0;
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid var(--line);
  text-align: left;
}

.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.section-head--compact {
  margin-bottom: 10px;
}

.note-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.note-head h2 {
  margin: 0;
  color: var(--ink-900);
  font-size: var(--fs-sm);
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1.3;
}

.note-action {
  min-height: auto;
  padding: 0;
  border: 0;
  background: transparent;
  font-size: var(--fs-xs);
}

.note-action:hover {
  background: transparent;
}

.profile-note .description {
  text-align: left;
}

.section-kicker {
  margin: 0 0 4px;
  color: var(--muted-soft);
  font-size: var(--fs-eyebrow);
  font-weight: 700;
  letter-spacing: var(--tracking-eyebrow);
  line-height: 1.2;
  text-transform: uppercase;
}

.section-head h2 {
  margin: 0;
  color: var(--ink-900);
  font-size: var(--fs-h2);
  font-weight: 700;
  letter-spacing: var(--tracking-tight);
  line-height: 1.25;
}

.text-action {
  flex: 0 0 auto;
  min-height: 32px;
  padding: 7px 12px;
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-sm);
  background: var(--surface);
  color: var(--accent-600);
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
  font-weight: 700;
  transition: background-color var(--dur) var(--ease),
    border-color var(--dur) var(--ease), box-shadow var(--dur) var(--ease);
}

.text-action:hover {
  border-color: var(--accent-600);
  background: var(--accent-050);
}

.text-action:focus-visible {
  box-shadow: var(--ring);
}

.profile-main {
  display: grid;
  gap: 16px;
  min-width: 0;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 18px;
}

.profile-field--wide {
  grid-column: span 2;
}

.profile-primary-action,
.security-button {
  min-height: 38px;
  white-space: nowrap;
}

.security-list {
  display: grid;
  gap: 0;
}

.security-row {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 12px 0;
}

.security-row + .security-row {
  border-top: 1px solid var(--line);
}

.security-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: var(--radius-sm);
  background: var(--accent-050);
  color: var(--accent-600);
  font-size: 18px;
}

.security-copy {
  min-width: 0;
}

.security-copy h3 {
  margin: 0 0 5px;
  color: var(--ink-900);
  font-size: var(--fs-body);
  font-weight: 700;
}

.security-copy p {
  margin: 0;
  color: var(--muted);
  font-size: var(--fs-sm);
}

.password-mask {
  letter-spacing: 0.08em;
}

.active,
.inactive,
.current-session,
.session-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  padding: 3px 9px;
  border-radius: var(--radius-sm);
  font-size: var(--fs-xs);
  font-weight: 700;
  line-height: 1;
}

.active {
  background: rgba(31, 122, 82, 0.12);
  color: var(--success);
}

.inactive {
  background: var(--danger-bg);
  color: var(--danger);
}

.session-count {
  min-width: 28px;
  background: var(--accent-050);
  color: var(--accent-600);
}

.session-list {
  display: grid;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}

.session-card {
  display: grid;
  grid-template-columns: minmax(180px, 1.1fr) minmax(220px, 1.4fr) auto;
  gap: 12px;
  align-items: center;
  min-width: 0;
  padding: 10px 0;
  border-bottom: 1px solid var(--line);
}

.session-card:last-child {
  border-bottom: 0;
}

.session-card-head {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  min-width: 0;
}

.session-device {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
}

.session-device-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--accent-600);
  font-size: 16px;
}

.session-device h3,
.session-device p {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.session-device h3 {
  margin: 0 0 2px;
  color: var(--ink-900);
  font-size: var(--fs-sm);
  font-weight: 700;
}

.session-device p {
  margin: 0;
  color: var(--muted);
  font-size: var(--fs-xs);
}

.current-session {
  flex: 0 0 auto;
  min-height: 20px;
  padding: 2px 7px;
  background: rgba(31, 122, 82, 0.12);
  color: var(--success);
  font-size: var(--fs-eyebrow);
}

.session-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  min-width: 0;
  margin: 0;
}

.session-meta div {
  display: inline-flex;
  gap: 5px;
  align-items: baseline;
  min-width: 0;
  max-width: 100%;
}

.session-meta span {
  flex: 0 0 auto;
  color: var(--muted-soft);
  font-size: var(--fs-eyebrow);
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1.2;
  text-transform: uppercase;
}

.session-meta strong {
  min-width: 0;
  color: var(--text);
  font-size: var(--fs-xs);
  font-weight: 600;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  overflow-wrap: anywhere;
}

.session-actions {
  display: flex;
  justify-content: flex-end;
  margin: 0;
}

.session-logout {
  min-width: 78px;
  min-height: 30px;
  padding: 6px 11px;
}

.two-factor-modal,
.password-modal {
  padding: 6px 6px 0;
}

.two-factor-modal label {
  display: block;
  margin-top: 18px;
  color: var(--muted);
  font-size: var(--fs-sm);
  font-weight: 600;
}

.qr-code {
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 12px;
  border: 1px dashed var(--line-strong);
  border-radius: var(--radius-md);
  background: var(--surface-2);
}

.qr-code img {
  width: min(240px, 100%);
  height: auto;
  border-radius: var(--radius-sm);
}

.error {
  margin-top: 8px;
  color: var(--danger);
  font-size: var(--fs-sm);
  font-weight: 600;
}

@media screen and (max-width: 1100px) {
  .profile-shell {
    grid-template-columns: 1fr;
  }

  .profile-identity {
    position: static;
  }
}

@media screen and (max-width: 760px) {
  .profile-shell {
    gap: 16px;
    padding: 16px;
  }

  .profile-identity {
    position: static;
  }

  .identity-card,
  .profile-section {
    padding: 18px;
  }

  .section-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }

  .session-card {
    grid-template-columns: 1fr;
    gap: 7px;
    padding: 11px 0;
  }

  .session-card-head {
    flex-wrap: wrap;
    justify-content: space-between;
  }

  .session-meta {
    gap: 4px 10px;
  }

  .profile-field--wide {
    grid-column: auto;
  }

  .profile-primary-action,
  .security-button {
    width: 100%;
  }

  .security-row {
    grid-template-columns: 40px minmax(0, 1fr);
  }

  .security-row .button {
    grid-column: 1 / -1;
  }

  .session-actions {
    justify-content: flex-start;
  }

  .session-logout {
    width: auto;
  }
}
</style>
